'use client'

import { BasicModal } from '@/components/modals/BasicModal'
import { NewsFavicon } from '@/components/NewsFavicon'
import Tooltip from '@/components/tooltip'
import OrderToggle from '@/components/ui/OrderToggle'
import { BiasDot } from '@/components/ui/SourceMeta'
import { track, trackOutboundArticle } from '@/lib/tracking'
import { DEFAULT_ORDER, rankArticles } from '@/utils/articleOrder'
import { getBiasCategory, getBiasLabel, getReliabilityInfo } from '@/utils/bias'
import { articleTimeLabel } from '@/utils/date'
import { useMemo, useState } from 'react'
import { RiLock2Line } from 'react-icons/ri'

// The article list, as a plain props-driven panel: any surface can open it
// with a set of articles. Story pages drive it from TopicContext (see
// story-detail/ArticlesModal); the homepage dashboard drives it from local
// state. Bias filtering is optional — when no filter state is passed in the
// panel keeps its own, so a caller can just hand over articles and a title.
const BIAS_FILTERS = [
  {
    value: 'left',
    label: 'L',
    tooltip: 'Left-leaning outlets',
    borderColor: 'border-l-blue-500',
    activeBg: 'bg-blue-50 dark:bg-blue-950',
  },
  {
    value: 'center',
    label: 'C',
    tooltip: 'Center outlets',
    borderColor: 'border-l-zinc-500',
    activeBg: 'bg-zinc-100 dark:bg-zinc-900',
  },
  {
    value: 'right',
    label: 'R',
    tooltip: 'Right-leaning outlets',
    borderColor: 'border-l-red-500',
    activeBg: 'bg-red-50 dark:bg-red-950',
  },
]

const ALL = ['left', 'center', 'right']

export function ArticleRow({ article, trackingContext }) {
  const biasLabel = getBiasLabel(article.outlet?.bias ?? 0)
  const reliabilityInfo = getReliabilityInfo(article.outlet?.reliability ?? 0)
  // Same "3h" / "Oct 9" as the headline columns. The panel only renders
  // after a click, so reading the clock here is safe.
  const time = article.time || articleTimeLabel(article.published_date)
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackOutboundArticle(article, trackingContext)}
      className="group block p-3 rounded-sm border border-border hover:bg-muted/30 transition-all"
    >
      <div className="flex items-start gap-3">
        <NewsFavicon
          src={`${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/public_assets/news-favicons/${article.outlet?.slug}.jpg`}
          alt={article.outlet?.name}
          faviconSize="sm"
          className="flex-shrink-0 mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground leading-snug line-clamp-2 group-hover:text-accent transition-colors">
            {article.title}
          </p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <span className="text-xs text-muted-foreground">
              {article.outlet?.name}
            </span>
            <span className={`text-xs font-medium ${biasLabel.color}`}>
              {biasLabel.label}
            </span>
            <Tooltip message={reliabilityInfo.description}>
              <span className="flex items-center">
                <BiasDot
                  bias={article.outlet?.bias}
                  reliability={article.outlet?.reliability}
                />
              </span>
            </Tooltip>
            {article.outlet?.is_paywalled && (
              <Tooltip message="Paywalled">
                <span>
                  <RiLock2Line className="w-3 h-3 text-muted-foreground" />
                </span>
              </Tooltip>
            )}
            {time && (
              <span
                className="ml-auto text-xs tabular-nums text-muted-foreground"
                title={time.title}
              >
                {time.label}
              </span>
            )}
          </div>
        </div>
      </div>
    </a>
  )
}

export default function ArticlesPanel({
  isOpen,
  onClose,
  title,
  articles = [],
  biasFilters: filtersProp,
  selectBias: selectProp,
  showFilters = true,
  storySlug = null,
}) {
  const [localFilters, setLocalFilters] = useState(ALL)
  const [order, setOrder] = useState(DEFAULT_ORDER)
  const biasFilters = filtersProp ?? localFilters
  // Local fallback tracks its own bias clicks; the TopicContext path
  // (selectProp) already tracks inside selectBias.
  const selectBias =
    selectProp ??
    ((value) => {
      const clearing = localFilters.length === 1 && localFilters[0] === value
      track('bias_filter_selected', {
        bias: clearing ? 'clear' : value,
        panel_title: title,
      })
      setLocalFilters(clearing ? ALL : [value])
    })

  const filtered = useMemo(
    () =>
      rankArticles(
        articles.filter((a) =>
          biasFilters.includes(getBiasCategory(a.outlet?.bias ?? 0))
        ),
        order
      ),
    [articles, biasFilters, order]
  )

  return (
    <BasicModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className="max-w-lg"
    >
      {showFilters && (
        <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-3 bg-background border-b border-border">
          <div className="flex gap-0.5">
            {BIAS_FILTERS.map((filter) => {
              const isSelected =
                biasFilters.length === 1 && biasFilters[0] === filter.value
              return (
                <button
                  key={filter.value}
                  onClick={() => selectBias(filter.value)}
                  aria-pressed={isSelected}
                  className={`w-8 h-7 text-xs font-bold border-l-[3px] rounded-r transition-colors ${
                    isSelected
                      ? `${filter.borderColor} ${filter.activeBg} text-foreground`
                      : 'border-l-transparent bg-muted text-muted-foreground'
                  }`}
                >
                  {filter.label}
                </button>
              )
            })}
          </div>
          <OrderToggle
            value={order}
            onChange={setOrder}
            surface="articles_panel"
            className="ml-auto"
          />
          <span className="text-sm text-muted-foreground">
            {filtered.length} of {articles.length}
          </span>
        </div>
      )}
      {!showFilters && (
        <div className="flex items-center justify-end px-4 pt-3">
          <OrderToggle
            value={order}
            onChange={setOrder}
            surface="articles_panel"
          />
        </div>
      )}

      <div className="p-4 space-y-2">
        {filtered.map((article) => (
          <ArticleRow
            key={article.id}
            article={article}
            trackingContext={{
              surface: 'articles_panel',
              panel_title: title,
              story_slug: storySlug,
            }}
          />
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            No articles match filters
          </p>
        )}
      </div>
    </BasicModal>
  )
}
